/**
 * Known accounts — a small persisted list of accounts that have signed in
 * on this device. Powers the "switch user" picker shown by the auth gate so
 * coaches who work for multiple clubs don't have to type their email every
 * time.
 *
 * Storage backend:
 *   • Both web and Tauri use `localStorage`. The Tauri webview shares its
 *     localStorage with the OS-managed webview profile, scoped to the
 *     installed application, which is good enough for non-secret hints like
 *     emails and org names.
 *   • Passwords are NEVER persisted. Re-prompting on switch is intentional
 *     (decision B) — we keep the picker as a UX accelerator only.
 *   • If a future phase needs to cache secrets (e.g. encrypted tokens), swap
 *     this implementation for the Stronghold plugin behind the same API.
 */

export interface KnownAccount {
  email: string;
  name: string | null;
  image: string | null;
  orgName: string | null;
  orgId: string | null;
  lastUsedAt: number;
}

const STORAGE_KEY = "coach.known-accounts";
const MAX_ACCOUNTS = 6;

function safeRead(): KnownAccount[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (a): a is KnownAccount =>
        typeof a === "object" && a !== null && typeof (a as { email?: unknown }).email === "string",
    );
  } catch {
    return [];
  }
}

function safeWrite(list: KnownAccount[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* quota / disabled — silently ignore, it's just a hint cache */
  }
}

export function listKnownAccounts(): KnownAccount[] {
  return safeRead().sort((a, b) => b.lastUsedAt - a.lastUsedAt);
}

export function recordKnownAccount(
  account: Omit<KnownAccount, "lastUsedAt"> & { lastUsedAt?: number },
): void {
  const email = account.email.trim().toLowerCase();
  if (!email) return;
  const list = safeRead().filter((a) => a.email !== email);
  list.unshift({
    email,
    name: account.name ?? null,
    image: account.image ?? null,
    orgName: account.orgName ?? null,
    orgId: account.orgId ?? null,
    lastUsedAt: account.lastUsedAt ?? Date.now(),
  });
  safeWrite(list.slice(0, MAX_ACCOUNTS));
}

export function forgetKnownAccount(email: string): void {
  const normalised = email.trim().toLowerCase();
  safeWrite(safeRead().filter((a) => a.email !== normalised));
}
